import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Link2, Copy, Check, Send } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { fmtDate } from '../lib/format';
import api from '../lib/api';

/**
 * RM tool: create/reveal a public pre-meeting form link tied to this session.
 * The link opens `/prefill/:sid?token=...` — no login required for the client.
 */
export const PrefillLinkButton = ({ session, canEdit, onUpdate }) => {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [url, setUrl] = useState(session?.prefill_token
    ? `${window.location.origin}/prefill/${session.id}?token=${session.prefill_token}`
    : '');

  const submittedAt = session?.prefill_submitted_at;
  const openedAt = session?.prefill_link_opened_at;
  const remindedAt = session?.prefill_reminded_at;

  const generate = async (regenerate = false) => {
    if (!session?.id) return;
    setBusy(true);
    try {
      const res = await api.createPrefillLink(session.id, regenerate);
      setUrl(res.url);
      if (onUpdate) onUpdate((s) => ({ ...s, prefill_token: res.token }));
      toast({
        title: regenerate ? 'New link created — old link is now invalid' : 'Pre-meeting form link ready',
        description: 'Share this link with your client.'
      });
    } catch (e) {
      toast({ title: 'Could not create link', description: e.apiMessage || 'Try again', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({ title: 'Link copied to clipboard' });
  };

  if (!canEdit && !url && !submittedAt) return null;

  return (
    <Card className="border-brand-border" data-testid="prefill-link-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <Send className="h-4 w-4 text-brand-indigo" />
          <div className="text-sm font-semibold text-brand-indigo">Client Pre-Meeting Form</div>
          {submittedAt && (
            <span data-testid="prefill-submitted-chip" className="ml-auto text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-700">
              Submitted · {fmtDate(submittedAt)}
            </span>
          )}
          {!submittedAt && openedAt && (
            <span data-testid="prefill-opened-chip" className="ml-auto text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full border border-brand-indigo/40 bg-brand-bg text-brand-indigo">
              Opened · {fmtDate(openedAt)}
            </span>
          )}
          {!submittedAt && !openedAt && remindedAt && (
            <span data-testid="prefill-reminded-chip" className="ml-auto text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full border border-amber-300 bg-amber-50 text-amber-700">
              Reminded · {fmtDate(remindedAt)}
            </span>
          )}
        </div>
        <div className="text-[12px] text-muted-foreground leading-relaxed">
          Share a short pre-meeting form so the client can fill in age, city, income & goals before the session. Their responses land straight into this MRI, saving 10 minutes on the call.
        </div>

        {url ? (
          <div className="mt-4">
            <div className="flex items-stretch gap-2">
              <div className="flex-1 flex items-center gap-2 h-10 px-3 rounded-md border border-brand-border bg-brand-bg font-mono text-[11px] text-brand-indigo overflow-x-auto whitespace-nowrap" data-testid="prefill-url">
                <Link2 className="h-3.5 w-3.5 flex-shrink-0" />
                {url}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={copy}
                className="border-brand-border h-10"
                data-testid="prefill-copy"
              >
                {copied ? <><Check className="h-4 w-4 mr-1 text-emerald-600" /> Copied</> : <><Copy className="h-4 w-4 mr-1" /> Copy</>}
              </Button>
            </div>
            {canEdit && (
              <button
                type="button"
                onClick={() => generate(true)}
                disabled={busy}
                className="mt-2 text-[11px] text-brand-indigo hover:underline disabled:opacity-50"
                data-testid="prefill-regenerate"
              >
                Regenerate link (invalidates the old one)
              </button>
            )}
          </div>
        ) : canEdit ? (
          <Button
            onClick={() => generate(false)}
            disabled={busy}
            className="mt-4 bg-brand-indigo hover:bg-brand-indigoLight text-white"
            data-testid="prefill-generate"
          >
            <Link2 className="h-4 w-4 mr-2" /> {busy ? 'Creating…' : 'Generate pre-meeting form link'}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default PrefillLinkButton;
